/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {applicantLogin, ApplicantLoginParams} from '../core/_requests'
import {useAuth} from '../core/AuthContext'

const loginSchema = Yup.object().shape({
  registration_no: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .required('Registration Number is required'),
  password: Yup.string().min(3, 'Minimum 3 symbols').required('Password is required'),
})

const initialValues: ApplicantLoginParams = {
  registration_no: process.env.NODE_ENV === 'development' ? 'user800' : '',
  password: process.env.NODE_ENV === 'development' ? 'P@55w0rd' : '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function ApplicantLogin() {
  const {login, logout, school} = useAuth()
  const [loading, setLoading] = useState(false)
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        if (!values.school_id) values.school_id = school?.id
        const data = await applicantLogin({...values})
        if (data?.token_type === 'Bearer') login(data?.auth_token, data?.refresh_token)
      } catch (error: any) {
        console.error(error)
        logout()
        setStatus(error.response.data.message || 'The login detail is incorrect')
        setSubmitting(false)
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <>
        {/* begin::Heading */}
        <div className='text-center mb-10'>
          <h1 className='text-dark mb-3'>Sign In {school ? 'to ' + school.name : ''}</h1>
        </div>
        {/* begin::Heading */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <label className='form-label fs-6 fw-bolder text-dark'>Registration Number</label>
          <input
            {...formik.getFieldProps('registration_no')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {'is-invalid': formik.touched.registration_no && formik.errors.registration_no},
              {
                'is-valid': formik.touched.registration_no && !formik.errors.registration_no,
              }
            )}
            type='text'
            name='registration_no'
            autoComplete='off'
          />
          {formik.touched.registration_no && formik.errors.registration_no && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.registration_no}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-10'>
          <div className='d-flex justify-content-between mt-n5'>
            <div className='d-flex flex-stack mb-2'>
              {/* begin::Label */}
              <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
              {/* end::Label */}
            </div>
          </div>
          <input
            type='password'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Action */}
        <div className='text-center'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Continue</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Action */}
      </>
    </form>
  )
}
